"use client"

import { useState, useEffect } from "react"
import { ChakraProvider, extendTheme, ColorModeScript, withDefaultColorScheme } from "@chakra-ui/react"

// Color mode configuration
const config = {
  initialColorMode: "light",
  useSystemColorMode: false,
}

// Define fonts
const fonts = {
  heading: `'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"`,
  body: `'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"`,
}

// Define custom colors
const colors = {
  primary1: "#58b6c0",
  primary2: "#04aec1",
  secondary1: "#58c087",
  secondary2: "#74ebd5",
  textActive: "white",
  textInactive: "black",
  // Dark mode colors
  darkPrimary1: "#3a8a91",
  darkPrimary2: "#027d8c",
  darkSecondary1: "#3a8a5a",
  darkSecondary2: "#4ba88e",
  // Gradients
  gradients: {
    primary: "linear(to-r, #58b6c0, #04aec1)",
    secondary: "linear(to-r, #04aec1, #58b6c0)",
    darkPrimary: "linear(to-r, #3a8a91, #027d8c)",
    darkSecondary: "linear(to-r, #027d8c, #3a8a91)",
  },
  // Custom gray scale
  custom: {
    50: "#FFFFFF",
    100: "#f2f2f2",
    200: "#dbdbdb",
    300: "#eaeaea",
    400: "#D7DDE8",
    500: "#eef2f3",
    600: "#a0aec0",
    700: "#718096",
    800: "#2d3748",
    900: "#1a202c",
  },
}

// Define text styles
const textStyles = {
  smallText: {
    fontSize: "8px",
  },
  h1: {
    fontSize: ["2xl", "3xl"],
    fontWeight: "bold",
    lineHeight: "110%",
    letterSpacing: "-0.01em",
  },
  h2: {
    fontSize: ["xl", "2xl"],
    fontWeight: "semibold",
    lineHeight: "110%",
    letterSpacing: "-0.01em",
  },
  subtitle: {
    fontSize: ["sm", "md"],
    fontWeight: "medium",
    lineHeight: "110%",
    letterSpacing: "0.01em",
    color: "gray.600",
    _dark: {
      color: "gray.400",
    },
  },
}

// Define component styles
const components = {
  Button: {
    baseStyle: {
      fontWeight: "500",
      borderRadius: "md",
      _focus: {
        boxShadow: "outline",
      },
      _hover: {
        transform: "translateY(-2px)",
        boxShadow: "md",
      },
      transition: "all 0.3s ease",
    },
    variants: {
      active: {
        borderColor: "primary1",
        borderWidth: "2px",
        color: "primary1",
        _dark: {
          borderColor: "darkPrimary1",
          color: "darkPrimary1",
        },
      },
      primary: {
        bg: "primary1",
        color: "white",
        _hover: {
          bg: "primary2",
          transform: "translateY(-2px)",
          boxShadow: "md",
        },
        _dark: {
          bg: "darkPrimary1",
          _hover: {
            bg: "darkPrimary2",
          },
        },
      },
      secondary: {
        bg: "secondary1",
        color: "white",
        _hover: {
          bg: "secondary2",
          transform: "translateY(-2px)",
          boxShadow: "md",
        },
        _dark: {
          bg: "darkSecondary1",
          _hover: {
            bg: "darkSecondary2",
          },
        },
      },
      outline: {
        borderColor: "primary1",
        color: "primary1",
        _hover: {
          bg: "rgba(88, 182, 192, 0.1)",
          transform: "translateY(-2px)",
          boxShadow: "md",
        },
        _dark: {
          borderColor: "darkPrimary1",
          color: "darkPrimary1",
          _hover: {
            bg: "rgba(58, 138, 145, 0.1)",
          },
        },
      },
    },
    defaultProps: {
      variant: "primary",
    },
  },
  Card: {
    baseStyle: {
      container: {
        borderRadius: "lg",
        boxShadow: "md",
        overflow: "hidden",
        transition: "all 0.3s ease",
        _hover: {
          boxShadow: "lg",
          transform: "translateY(-4px)",
        },
      },
    },
  },
  Link: {
    baseStyle: {
      transition: "all 0.3s ease",
      _hover: {
        textDecoration: "none",
      },
    },
  },
  Input: {
    variants: {
      filled: {
        field: {
          borderRadius: "md",
          bg: "gray.100",
          _hover: {
            bg: "gray.200",
          },
          _dark: {
            bg: "whiteAlpha.100",
            _hover: {
              bg: "whiteAlpha.200",
            },
          },
        },
      },
      outline: {
        field: {
          borderRadius: "md",
          _focus: {
            borderColor: "primary1",
            boxShadow: `0 0 0 1px ${colors.primary1}`,
          },
          _dark: {
            _focus: {
              borderColor: "darkPrimary1",
              boxShadow: `0 0 0 1px ${colors.darkPrimary1}`,
            },
          },
        },
      },
    },
  },
  Menu: {
    baseStyle: {
      list: {
        borderRadius: "md",
        boxShadow: "lg",
        p: 2,
      },
      item: {
        borderRadius: "md",
        _hover: {
          bg: "gray.100",
        },
        _dark: {
          _hover: {
            bg: "whiteAlpha.100",
          },
        },
      },
    },
  },
  Tooltip: {
    baseStyle: {
      borderRadius: "md",
      bg: "gray.800",
      color: "white",
      px: 3,
      py: 2,
      boxShadow: "md",
      _dark: {
        bg: "gray.200",
        color: "gray.800",
      },
    },
  },
}

// Define global styles
const styles = {
  global: (props) => ({
    body: {
      bg: props.colorMode === "dark" ? "gray.900" : "gray.50",
      color: props.colorMode === "dark" ? "white" : "gray.800",
      transition: "background-color 0.3s ease, color 0.3s ease",
    },
    // Add smooth scrolling
    html: {
      scrollBehavior: "smooth",
    },
    // Custom scrollbar
    "::-webkit-scrollbar": {
      width: "8px",
      height: "8px",
    },
    "::-webkit-scrollbar-track": {
      background: props.colorMode === "dark" ? "gray.800" : "gray.100",
    },
    "::-webkit-scrollbar-thumb": {
      background: props.colorMode === "dark" ? "gray.600" : "gray.400",
      borderRadius: "full",
    },
    "::-webkit-scrollbar-thumb:hover": {
      background: props.colorMode === "dark" ? "gray.500" : "gray.500",
    },
    // Focus outline for accessibility
    "*:focus": {
      outline: "2px solid",
      outlineColor: props.colorMode === "dark" ? "darkPrimary1" : "primary1",
      outlineOffset: "2px",
    },
    // Transitions for all elements
    "*": {
      transition: "color 0.3s ease, border-color 0.3s ease, background-color 0.3s ease",
    },
  }),
}

// Create the custom theme
const customTheme = extendTheme(
  {
    config,
    colors,
    fonts,
    textStyles,
    components,
    styles,
  },
  withDefaultColorScheme({ colorScheme: "blue" }),
)

const ChakraWrapper = ({ children }) => {
  const [isLoaded, setIsLoaded] = useState(false)

  // Add a small delay to ensure smooth transitions when the app loads
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoaded(true)
    }, 100)
    return () => clearTimeout(timer)
  }, [])

  return (
    <>
      <ColorModeScript initialColorMode={customTheme.config.initialColorMode} />
      <ChakraProvider theme={customTheme}>
        <div
          style={{
            opacity: isLoaded ? 1 : 0,
            transition: "opacity 0.5s ease",
          }}
        >
          {children}
        </div>
      </ChakraProvider>
    </>
  )
}

export default ChakraWrapper
